/**
 * Lazy import wrapper with automatic retry on chunk load failure
 * 
 * Handles "Failed to fetch dynamically imported module" errors that occur when:
 * - New deployment with different chunk hashes
 * - Browser has old HTML cached
 * - Network issues during chunk loading
 * 
 * Solution: Detect chunk errors and reload page once to get fresh HTML
 */

import { lazy, ComponentType } from 'react';

const RELOAD_KEY = 'chunk_reload_attempted';
const RELOAD_TIMEOUT = 5000; // 5 seconds

/**
 * Check if error is a chunk loading error
 */
function isChunkLoadError(error: unknown): boolean {
  const errorMessage = String(error);
  return (
    errorMessage.includes('Failed to fetch dynamically imported module') ||
    errorMessage.includes('Importing a module script failed') ||
    errorMessage.includes('Failed to load module script') ||
    errorMessage.includes('ChunkLoadError')
  );
}

/**
 * Enhanced lazy import with automatic retry on chunk errors
 * 
 * @param importFn - Dynamic import function
 * @returns Lazy component with retry logic
 * 
 * @example
 * const HomePage = lazyWithRetry(() => import('./pages/Home/HomePage'));
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      // Clear reload flag after timeout (prevents infinite loops)
      const reloadTime = sessionStorage.getItem(RELOAD_KEY);
      if (reloadTime) {
        const elapsed = Date.now() - parseInt(reloadTime, 10);
        if (elapsed > RELOAD_TIMEOUT) {
          sessionStorage.removeItem(RELOAD_KEY);
        }
      }

      return await importFn();
    } catch (error) {
      // Check if chunk load error and haven't reloaded yet
      if (isChunkLoadError(error)) {
        const hasReloaded = sessionStorage.getItem(RELOAD_KEY);
        
        if (!hasReloaded) {
          console.warn('[LazyRetry] Chunk load error detected, reloading page...', error);
          sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
          
          // Hard reload to get fresh HTML and chunks
          window.location.reload();
          
          // Return a never-resolving promise to prevent error propagation
          return new Promise(() => {});
        } else {
          console.error('[LazyRetry] Chunk load error persists after reload', error);
        }
      }
      
      // Re-throw non-chunk errors or if already reloaded
      throw error;
    }
  });
}

/**
 * Retry a dynamic import manually (for non-lazy imports)
 * 
 * @param importFn - Dynamic import function
 * @param maxRetries - Maximum number of retries (default: 3)
 * @param delayMs - Delay between retries in milliseconds (default: 1000)
 * @returns Promise resolving to the imported module
 * 
 * @example
 * const module = await retryDynamicImport(() => import('./heavy-module'));
 */
export async function retryDynamicImport<T>(
  importFn: () => Promise<T>,
  maxRetries: number = 3,
  delayMs: number = 1000
): Promise<T> {
  let lastError: unknown;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await importFn();
    } catch (error) {
      lastError = error;
      
      if (!isChunkLoadError(error)) {
        throw error; // Not a chunk error, fail immediately
      }
      
      if (attempt < maxRetries) {
        console.warn(`[RetryImport] Attempt ${attempt + 1}/${maxRetries} failed, retrying...`);
        await new Promise(resolve => setTimeout(resolve, delayMs * (attempt + 1)));
      }
    }
  }
  
  throw lastError;
}
